import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { Calendar, Clock, Send } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BookAppointment = () => {
  const { token } = useAuth();
  const { t } = useLanguage();
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [requests, setRequests] = useState([]);

  useEffect(() => { fetchRequests(); }, []);
  const fetchRequests = async () => { try { const res = await axios.get(`${API}/appointments/my-requests`, { headers: { Authorization: `Bearer ${token}` } }); setRequests(res.data); } catch (e) { console.error(e); } };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!date || !time) { toast.error(t.preferredDate + ' & ' + t.preferredTime); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/appointments/request`, { requested_date: date, requested_time: time, notes: notes || null }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(t.sendRequest + ' - OK!'); setDate(''); setTime(''); setNotes(''); fetchRequests();
    } catch (e) { toast.error('Failed'); } finally { setSubmitting(false); }
  };

  const statusStyle = (s) => ({ pending: 'bg-amber-100 text-amber-800', approved: 'bg-emerald-100 text-emerald-800', denied: 'bg-red-100 text-red-800', counter_proposed: 'bg-blue-100 text-blue-800', confirmed: 'bg-emerald-100 text-emerald-800', rejected_by_patient: 'bg-gray-100 text-gray-800' }[s] || 'bg-gray-100 text-gray-800');
  const statusLabel = (s) => ({ pending: t.statusPending, approved: t.statusApproved, denied: t.statusDenied, counter_proposed: t.statusCounterProposed, confirmed: t.statusConfirmed, rejected_by_patient: t.statusDeclined }[s] || s);

  return (
    <div className="p-6 lg:p-8" data-testid="book-appointment-page">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-[#1F2937] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>{t.bookAppointment}</h1>
        <p className="text-[#6B7280]">{t.bookAppointmentSubtitle}</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6">
          <h2 className="text-lg font-bold text-[#1F2937] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>{t.newRequest}</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[#1F2937] mb-2"><Calendar className="w-4 h-4 inline mr-1" /> {t.preferredDate}</label>
              <input type="date" value={date} onChange={e => setDate(e.target.value)} min={new Date().toISOString().split('T')[0]} required className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0F4C5C]/20 focus:border-[#0F4C5C]" data-testid="appt-date" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#1F2937] mb-2"><Clock className="w-4 h-4 inline mr-1" /> {t.preferredTime}</label>
              <input type="time" value={time} onChange={e => setTime(e.target.value)} required className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0F4C5C]/20 focus:border-[#0F4C5C]" data-testid="appt-time" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#1F2937] mb-2">{t.notesOptional}</label>
              <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3} className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0F4C5C]/20 focus:border-[#0F4C5C] resize-none" placeholder="..." data-testid="appt-notes" />
            </div>
            <button type="submit" disabled={submitting} className="w-full bg-[#0F4C5C] text-white hover:bg-[#0F4C5C]/90 rounded-full px-6 py-3 font-semibold transition-all shadow-lg disabled:opacity-50 inline-flex items-center justify-center gap-2" data-testid="submit-appt">
              <Send className="w-4 h-4" /> {submitting ? t.sending : t.sendRequest}
            </button>
          </form>
        </div>

        <div>
          <h2 className="text-lg font-bold text-[#1F2937] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>{t.myRequests}</h2>
          {requests.length === 0 ? (
            <div className="bg-white rounded-lg border border-slate-200 p-8 text-center text-[#6B7280]">{t.noRequestsYet}</div>
          ) : (
            <div className="space-y-3">
              {requests.map(r => (
                <div key={r.id} className="bg-white rounded-lg border border-slate-200 p-4 shadow-sm" data-testid={`request-${r.id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2 text-sm">
                      <Calendar className="w-4 h-4 text-[#6B7280]" />
                      <span className="font-medium text-[#1F2937]">{new Date(r.requested_date).toLocaleDateString()}</span>
                      <Clock className="w-4 h-4 text-[#6B7280] ml-2" />
                      <span className="text-[#1F2937]">{r.requested_time}</span>
                    </div>
                    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${statusStyle(r.status)}`}>{statusLabel(r.status)}</span>
                  </div>
                  {r.notes && <p className="text-sm text-[#6B7280]">{r.notes}</p>}
                  {r.proposed_date && (
                    <div className="mt-2 text-sm bg-blue-50 p-2 rounded text-blue-800">{t.clinicProposed} {new Date(r.proposed_date).toLocaleDateString()} {t.at} {r.proposed_time}</div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BookAppointment;
